import { useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { type FunctionComponent, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
	getPasswordChecks,
	PASSWORD_MIN_LENGTH,
} from "../../lib/password-rules";
import styles from "./password-validation.module.scss";

interface Props {
	password: string;
}

export const PasswordValidation: FunctionComponent<Props> = ({ password }) => {
	const { t } = useTranslation();
	const {
		isValidLength,
		hasUpperCase,
		hasLowerCase,
		hasNumbers,
		hasSpecialChar,
	} = getPasswordChecks(password);

	const strength = useMemo(
		() =>
			[
				isValidLength,
				hasUpperCase,
				hasLowerCase,
				hasNumbers,
				hasSpecialChar,
			].filter(Boolean).length,

		[isValidLength, hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChar],
	);

	const getProgress = () => (strength / 5) * 100;

	const progressValueStyles = clsx(styles.progress_value, {
		[styles.weak]: strength >= 2,
		[styles.medium]: strength >= 3,
		[styles.strong]: strength >= 5,
	});

	const navigate = useNavigate();
	useEffect(() => {
		navigate({
			to: "/login/temp",
			search: {
				strength: String(strength),
			},
			replace: true,
		});
	}, [strength, navigate]);

	if (password.trim() === "") {
		return null;
	}

	return (
		<div className={styles.container}>
			<div className={styles.progress}>
				<div
					style={{ width: `${getProgress()}%` }}
					className={progressValueStyles}
				></div>
			</div>
			<div className={styles.text_container}>
				<ul className={styles.list_container}>
					{strength !== 5 && (
						<span
							className={styles.list}
						>{`${t("login.temp_password.password_must_contain")} :`}</span>
					)}
					{strength === 5 && (
						<span className={styles.list}>
							{t("login.temp_password.password_is_safe_and_secure")}
						</span>
					)}
					{!isValidLength && (
						<li className={styles.list}>
							{t("login.temp_password.min_char", { min: PASSWORD_MIN_LENGTH })}
						</li>
					)}
					{!hasUpperCase && (
						<li className={styles.list}>
							{t("login.temp_password.capital_letter")}
						</li>
					)}
					{!hasLowerCase && (
						<li className={styles.list}>
							{t("login.temp_password.lower_case_letter")}
						</li>
					)}
					{!hasNumbers && (
						<li className={styles.list}>
							{t("login.temp_password.one_digit")}
						</li>
					)}
					{!hasSpecialChar && (
						<li className={styles.list}>
							{t("login.temp_password.special_character")}
						</li>
					)}
				</ul>
				{strength <= 2 && (
					<span className={styles.str}>{t("login.temp_password.weak")}</span>
				)}
				{strength > 2 && strength < 5 && (
					<span className={styles.str}>{t("login.temp_password.medium")}</span>
				)}
				{strength === 5 && (
					<span className={styles.str}>{t("login.temp_password.strong")}</span>
				)}
			</div>
		</div>
	);
};
